import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  apaAuthors,
  apaDate,
  apaInTextAuthors,
  type CitationParts,
  chicagoBibliographyAuthors,
  chicagoNoteAuthors,
  chicagoShortAuthors,
  mlaAuthors,
  mlaDate,
  mlaInTextAuthors,
  type PartialDate,
  type PersonName,
  parsePartialDate,
  tidyParts,
  withPeriod,
} from "@/lib/tool-kit/citation-parts";
import {
  quotedTitle,
  shortNoteTitle,
  talkLength,
  titleCase,
} from "@/lib/tool-kit/citation-sources-more/common";
import {
  capitalizeSubtitle,
  closer,
  get,
  italic,
  mlaUrl,
  plain,
} from "@/lib/tool-kit/legal-sources/common";
import {
  fullUrl,
  groupName,
  longDate,
  type MediaLabels,
  type MediaValues,
  parsePeople,
} from "@/lib/tool-kit/media-sources/common";

/*
 * A TED or TEDx talk watched on TED.com or on YouTube, in APA 7, MLA 9 and Chicago 18.
 *
 * Sources checked (October 2026):
 * - APA 7: the APA Style page "TED talk references" (blocked to automated readers;
 *   read through the University of Southern Cross and Augusta University guides that
 *   reproduce it). On ted.com the speaker is the author, TED Conferences the publisher,
 *   the date is year and month, [Video] follows the italic title: "Giertz, S. (2018,
 *   April). Why you should make useless things [Video]. TED Conferences.
 *   https://www.ted.com/talks/simone_giertz_why_you_should_make_useless_things",
 *   cited (Giertz, 2018). On YouTube the author is the account that uploaded the video,
 *   to help retrieval, the date includes the day and the source is YouTube: "TED.
 *   (2012, March 16). Listening to shame | Brené Brown [Video]. YouTube.
 *   https://www.youtube.com/watch?v=psN1DORYYV0", cited (TED, 2012). A quotation takes
 *   the time stamp where it starts: (Lipton, 2018, 00:05:40).
 * - MLA 9: no TED example in the Handbook; library guides apply the core elements to a
 *   web video — Allende, Isabel. "Tales of Passion." TED, Mar. 2007,
 *   www.ted.com/talks/isabel_allende_tales_of_passion?language=en. — and for a copy on
 *   YouTube add "uploaded by TED". A time stamp goes in the parentheses: (Allende 3:15).
 * - Chicago 18: the Notes-Bibliography Quick Guide (§14.167) has a TED sample — Cowan,
 *   Vaitea. "How Green Hydrogen Could End the Fossil Fuel Era." TED Talk, Vancouver,
 *   BC, April 2022. Video, 9 min., 15 sec. https://www.ted.com/talks/… — with the
 *   shortened note: Cowan, "Green Hydrogen," at 6:09–17. The YouTube form follows the
 *   Quick Guide's YouTube sample (event date, "posted … by …", YouTube, length). A TEDx
 *   talk uses the event's name in place of "TED Talk", the practice of library guides
 *   (Marist), not a CMOS example.
 */

export const TED_FIELDS = {
  speakers: "speakers",
  title: "title",
  youtubeTitle: "youtubeTitle",
  tedx: "tedx",
  location: "location",
  filmed: "filmed",
  posted: "posted",
  length: "length",
  timeStamp: "timeStamp",
  channel: "channel",
  shortTitle: "shortTitle",
  url: "url",
} as const;

const F = TED_FIELDS;

/** Where the talk was watched. */
export type TedKind = "ted" | "youtube";

export interface TedLabels extends MediaLabels {
  apaNote: string;
  apaYoutubeNote: string;
  mlaNote: string;
  chicagoNote: string;
}

interface Talk {
  kind: TedKind;
  speakers: PersonName[];
  title: string;
  youtubeTitle: string;
  tedx: string;
  location: string;
  filmed: PartialDate | null;
  posted: PartialDate | null;
  length: string;
  timeStamp: string;
  channel: string;
  shortTitle: string;
  url: string;
}

function readTalk(kind: TedKind, values: MediaValues): Talk {
  const youtube = kind === "youtube";
  return {
    kind,
    speakers: parsePeople(get(values, F.speakers)),
    title: capitalizeSubtitle(get(values, F.title)),
    youtubeTitle: youtube ? get(values, F.youtubeTitle) : "",
    tedx: get(values, F.tedx),
    location: get(values, F.location),
    filmed: parsePartialDate(get(values, F.filmed)),
    posted: parsePartialDate(get(values, F.posted)),
    length: get(values, F.length),
    timeStamp: get(values, F.timeStamp),
    channel: youtube ? get(values, F.channel) || "TED" : "",
    shortTitle: get(values, F.shortTitle),
    url: fullUrl(get(values, F.url)),
  };
}

/** The date a reader finds first: when it was posted, else when it was filmed. */
function citedDate(talk: Talk): PartialDate | null {
  return talk.posted ?? talk.filmed;
}

/* ---------------------------------------------------------------------- APA */

/** APA: the speaker on TED.com; the uploading channel on YouTube. */
function apaAuthorNames(talk: Talk): PersonName[] {
  return talk.kind === "youtube" ? [groupName(talk.channel)] : talk.speakers;
}

function apaYear(talk: Talk): string {
  const date = citedDate(talk);
  return date ? String(date.year) : "n.d.";
}

function apaReference(talk: Talk): CitationParts {
  const names = apaAuthorNames(talk);
  const date = apaDate(citedDate(talk));
  const publisher = talk.kind === "youtube" ? "YouTube" : "TED Conferences";
  const title = talk.youtubeTitle || talk.title;
  const tail = [`${publisher}.`, talk.url].filter(Boolean).join(" ");
  if (names.length === 0) {
    return tidyParts([italic(title), plain(` [Video]. (${date}). ${tail}`)]);
  }
  const authors = apaAuthors(names);
  return tidyParts([
    plain(`${authors}${closer(authors)} (${date}). `),
    italic(title),
    plain(` [Video]. ${tail}`),
  ]);
}

function apaOutput(talk: Talk, labels: TedLabels): CitationOutput {
  const names = apaAuthorNames(talk);
  const year = apaYear(talk);
  const stamp = talk.timeStamp ? `, ${talk.timeStamp}` : "";
  const short = (talk.youtubeTitle || talk.title).split(":")[0];
  const has = names.length > 0;
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      { label: labels.referenceList, parts: apaReference(talk) },
      {
        label: labels.parenthetical,
        parts: has
          ? [plain(`(${apaInTextAuthors(names)}, ${year}${stamp})`)]
          : [plain("("), italic(short), plain(`, ${year}${stamp})`)],
      },
      {
        label: labels.narrative,
        parts: has
          ? [plain(`${apaInTextAuthors(names, true)} (${year}${stamp})`)]
          : [italic(short), plain(` (${year}${stamp})`)],
      },
    ],
    note: talk.kind === "youtube" ? labels.apaYoutubeNote : labels.apaNote,
  };
}

/* ---------------------------------------------------------------------- MLA */

function mlaWorksCited(talk: Talk): CitationParts {
  const title = titleCase(talk.title);
  const date = mlaDate(citedDate(talk));
  const youtube = talk.kind === "youtube";
  const container = youtube ? "YouTube" : "TED";
  const rest = [
    youtube && talk.channel ? `uploaded by ${talk.channel}` : "",
    date,
    talk.url ? mlaUrl(talk.url) : "",
  ].filter(Boolean);
  const parts: CitationParts = [];
  if (talk.speakers.length > 0) {
    const authors = mlaAuthors(talk.speakers);
    parts.push(plain(`${authors}${closer(authors)} `));
  }
  parts.push(plain(`${quotedTitle(title, ".")} `), italic(container));
  parts.push(plain(`${rest.length > 0 ? `, ${rest.join(", ")}` : ""}.`));
  return tidyParts(parts);
}

function mlaOutput(talk: Talk, labels: TedLabels): CitationOutput {
  const who =
    talk.speakers.length > 0
      ? mlaInTextAuthors(talk.speakers)
      : `“${titleCase(talk.title).split(":")[0]}”`;
  const stamp = talk.timeStamp ? ` ${talk.timeStamp}` : "";
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      { label: labels.worksCited, parts: mlaWorksCited(talk) },
      { label: labels.inText, parts: [plain(`(${who}${stamp})`)] },
    ],
    note: labels.mlaNote,
  };
}

/* ------------------------------------------------------------------ Chicago */

function eventLabel(talk: Talk): string {
  return talk.tedx || "TED Talk";
}

/** "posted March 16, 2012, by TED" on YouTube; "posted …" only when dates differ. */
function chicagoPosting(talk: Talk): string {
  const posted = longDate(talk.posted);
  if (talk.kind === "youtube") {
    if (!posted) {
      return "";
    }
    return talk.channel
      ? `posted ${posted}, by ${talk.channel}`
      : `posted ${posted}`;
  }
  if (posted && talk.filmed && posted !== longDate(talk.filmed)) {
    return `posted ${posted}`;
  }
  return "";
}

function chicagoDate(talk: Talk): string {
  return longDate(talk.filmed ?? talk.posted);
}

function chicagoNote(talk: Talk, title: string): CitationParts {
  const youtube = talk.kind === "youtube";
  const elements = [
    eventLabel(talk),
    talk.location,
    chicagoDate(talk),
    chicagoPosting(talk),
    youtube ? "YouTube" : "",
    talkLength(talk.length),
    talk.url,
  ].filter(Boolean);
  const lead =
    talk.speakers.length > 0 ? `${chicagoNoteAuthors(talk.speakers)}, ` : "";
  return [
    plain(
      `${lead}${quotedTitle(title, ",")} ${withPeriod(elements.join(", "))}`
    ),
  ];
}

function chicagoShortNote(talk: Talk, title: string): CitationParts {
  const short = talk.shortTitle || shortNoteTitle(title);
  const locator = talk.timeStamp
    ? `${quotedTitle(short, ",")} at ${talk.timeStamp}.`
    : quotedTitle(short, ".");
  if (talk.speakers.length === 0) {
    return [plain(locator)];
  }
  return [plain(`${chicagoShortAuthors(talk.speakers)}, ${locator}`)];
}

function chicagoBibliography(talk: Talk, title: string): CitationParts {
  const youtube = talk.kind === "youtube";
  const sentences: string[] = [];
  if (talk.speakers.length > 0) {
    const authors = chicagoBibliographyAuthors(talk.speakers);
    sentences.push(`${authors}${closer(authors)}`);
  }
  sentences.push(quotedTitle(title, "."));
  const event = [eventLabel(talk), talk.location, chicagoDate(talk)]
    .filter(Boolean)
    .join(", ");
  sentences.push(`${event}.`);
  const posting = chicagoPosting(talk);
  if (posting) {
    sentences.push(
      withPeriod(`${posting.charAt(0).toUpperCase()}${posting.slice(1)}`)
    );
  }
  const length = talkLength(talk.length);
  if (youtube) {
    sentences.push(withPeriod(["YouTube", length].filter(Boolean).join(", ")));
  } else {
    sentences.push(withPeriod(["Video", length].filter(Boolean).join(", ")));
  }
  if (talk.url) {
    sentences.push(`${talk.url}.`);
  }
  return [plain(sentences.join(" "))];
}

function chicagoOutput(talk: Talk, labels: TedLabels): CitationOutput {
  const title = titleCase(talk.title);
  return {
    key: "chicago",
    label: labels.chicago,
    entries: [
      { label: labels.footnote, parts: chicagoNote(talk, title) },
      { label: labels.shortNote, parts: chicagoShortNote(talk, title) },
      { label: labels.bibliography, parts: chicagoBibliography(talk, title) },
    ],
    note: labels.chicagoNote,
  };
}

export function tedIsReady(values: MediaValues): boolean {
  return get(values, F.title) !== "";
}

export function formatTedTalk(
  kind: TedKind,
  values: MediaValues,
  labels: TedLabels
): CitationOutput[] {
  const talk = readTalk(kind, values);
  return [
    apaOutput(talk, labels),
    mlaOutput(talk, labels),
    chicagoOutput(talk, labels),
  ];
}
